import React, {useState} from 'react'
import {getSpielerName} from "./UebersichtBereich";
import {vorhanden} from "./statistikWerte";

// so viele Plätze je Karte, der Rest über "Alle anzeigen"
const ANZAHL_KURZ = 10;

/**
 * Highlights des Events im neuen Design: je Kategorie eine Karte mit Rangliste.
 * Die alte Ansicht verwendet weiterhin PublicTournamentHighlightsTab.
 *
 * @param markierterSpieler bei aktiver Spielersuche werden alle Einträge gezeigt, damit der Treffer gefunden wird
 */
export default function HighlightsBereich({tournamentWrapper, markierterSpieler}) {

    const wrapper = tournamentWrapper.ranglisteWrapper;
    if (!vorhanden(wrapper)) {
        return <div className="dhpt-statistik"><p className="dhpt-hinweis">Es sind noch keine Highlights vorhanden.</p></div>
    }

    const name = (id) => getSpielerName(tournamentWrapper, id) || "Unbekannt";

    const kategorien = [
        getAnzahlKategorie("180er", wrapper.rangliste180er, name),
        getAnzahlKategorie("171er", wrapper.rangliste171er, name),
        // 171 bis 180 als eigene Rubrik, in den Highscores stehen dann nur noch die übrigen Werte
        getWerteKategorie("171–180", wrapper.ranglisteHighscores, name, wert => wert >= 171 && wert <= 180),
        getWerteKategorie("Highfinishes", wrapper.ranglisteHighfinishes, name),
        getWerteKategorie("Highscores", wrapper.ranglisteHighscores, name, wert => !(wert >= 171 && wert <= 180)),
        getLowDartsKategorie(wrapper.lowDarts, name),
        getDartsKategorie("Best Darts", "bestes Leg", wrapper.bestDarts, name),
        getDartsKategorie("Worst Darts", "schlechtestes Leg", wrapper.worstDarts, name),
    ].filter(kategorie => kategorie.zeilen.length > 0);

    if (kategorien.length === 0) {
        return <div className="dhpt-statistik"><p className="dhpt-hinweis">Es sind noch keine Highlights vorhanden.</p></div>
    }

    return <div className="dhpt-statistik">
        <div className="dhpt-karten">
            {kategorien.map(kategorie => <HighlightKarte key={kategorie.titel} kategorie={kategorie} alleZeigen={vorhanden(markierterSpieler)}/>)}
        </div>
    </div>
}

function HighlightKarte({kategorie, alleZeigen}) {
    const [offen, setOffen] = useState(false);
    const zeilen = offen || alleZeigen ? kategorie.zeilen : kategorie.zeilen.slice(0, ANZAHL_KURZ);
    const listenId = "dhpt-hl-" + kategorie.titel.replace(/\s/g, "-").toLowerCase();

    return <section className="dhpt-karte">
        <h3 className="dhpt-karte-titel"><span>{kategorie.titel}</span>{kategorie.zusatz && <span className="dhpt-karte-zusatz">{kategorie.zusatz}</span>}</h3>
        <ol id={listenId} className="dhpt-hl-liste">
            {zeilen.map(zeile => <li key={zeile.id} className="dhpt-hl-zeile">
                {/* bewusst ohne Gold/Silber/Bronze, alle Plätze gleich */}
                <span className="dhpt-hl-platz dhpt-tab-platz-zahl">{zeile.platz}.</span>
                <span className="dhpt-hl-mitte">
                    <span className="dhpt-hl-name">{zeile.name}</span>
                    {zeile.details && zeile.details.length > 0 &&
                        <span className="dhpt-hl-details">
                            {zeile.details.map(detail => <span key={detail.wert} className="dhpt-hl-detail">
                                {detail.anzahl > 1 && <span className="dhpt-st-anzahl">{detail.anzahl}×</span>}{detail.wert}
                            </span>)}
                        </span>
                    }
                </span>
                <span className="dhpt-hl-wert">{zeile.wert}{zeile.einheit && <small>{zeile.einheit}</small>}</span>
            </li>)}
        </ol>
        {!alleZeigen && kategorie.zeilen.length > ANZAHL_KURZ &&
            <button type="button" className="dhpt-text-knopf" aria-expanded={offen} aria-controls={listenId} onClick={() => setOffen(!offen)}>
                <i className={offen ? "pi pi-chevron-up" : "pi pi-chevron-down"} aria-hidden="true"/>
                {offen ? "Weniger anzeigen" : "Alle " + kategorie.zeilen.length + " anzeigen"}
            </button>
        }
    </section>
}

/**
 * Plätze vergeben: gleiche Werte ergeben den gleichen Platz (1, 2, 2, 4). Die Reihenfolge kommt sortiert vom Backend.
 */
function mitPlatz(eintraege, vergleichswert) {
    let platz = 1;
    return eintraege.map((eintrag, index) => {
        if (index > 0 && vergleichswert(eintrag) !== vergleichswert(eintraege[index - 1])) {
            platz = index + 1;
        }
        return {...eintrag, platz};
    });
}

function liste(werte) {
    return Array.isArray(werte) ? werte : [];
}

function getAnzahlKategorie(titel, rangliste, name) {
    const eintraege = liste(rangliste);
    const gesamt = eintraege.reduce((summe, eintrag) => summe + eintrag.anzahl, 0);
    return {
        titel,
        zusatz: gesamt > 0 ? "gesamt " + gesamt : undefined,
        zeilen: mitPlatz(eintraege, e => e.anzahl).map(e => ({id: e.id, platz: e.platz, name: name(e.id), wert: e.anzahl})),
    };
}

// Highfinishes/Highscores: je Spieler die Anzahl, darunter die einzelnen Werte, z.B. "2× 140"
// filter: nur diese Werte übernehmen, z.B. 171-180 aus den Highscores; danach neu nach Anzahl sortiert
function getWerteKategorie(titel, rangliste, name, filter = () => true) {
    const eintraege = liste(rangliste).map(eintrag => {
        const werte = liste(eintrag.values).filter(wert => filter(Number(wert.key)));
        return {
            id: eintrag.id,
            gesamt: werte.reduce((summe, wert) => summe + wert.value, 0),
            details: werte.map(wert => ({wert: wert.key, anzahl: wert.value})).sort((a, b) => Number(b.wert) - Number(a.wert)),
        };
    }).filter(eintrag => eintrag.gesamt > 0)
        .sort((a, b) => b.gesamt - a.gesamt);
    const gesamt = eintraege.reduce((summe, eintrag) => summe + eintrag.gesamt, 0);
    return {
        titel,
        zusatz: gesamt > 0 ? "gesamt " + gesamt : undefined,
        zeilen: mitPlatz(eintraege, e => e.gesamt).map(e => ({id: e.id, platz: e.platz, name: name(e.id), wert: e.gesamt, details: e.details})),
    };
}

// Low Darts: bestes Leg und Anzahl der Legs mit wenigen Darts
function getLowDartsKategorie(lowDarts, name) {
    const eintraege = liste(lowDarts);
    return {
        titel: "Low Darts",
        zusatz: "Darts im besten Leg",
        zeilen: mitPlatz(eintraege, e => e.best + "/" + e.anzahlLowDarts).map(e => ({
            id: e.id, platz: e.platz, name: name(e.id), wert: e.best, einheit: "Darts",
            details: Object.entries(e.lowDarts || {})
                .map(([darts, anzahl]) => ({wert: darts + " Darts", anzahl, sortierung: Number(darts)}))
                .sort((a, b) => a.sortierung - b.sortierung),
        })),
    };
}

function getDartsKategorie(titel, zusatz, rangliste, name) {
    const eintraege = liste(rangliste);
    return {
        titel,
        zusatz: "Darts im " + zusatz,
        zeilen: mitPlatz(eintraege, e => e.anzahldarts).map(e => ({id: e.id, platz: e.platz, name: name(e.id), wert: e.anzahldarts, einheit: "Darts"})),
    };
}
